import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { PRESENTATION_MODE_KEY } from '@/App/constants/storageKeys';
import { appSpinnerMiddleware } from '@/App/middleware/appSpinnerMiddleware';
import { errorMiddleware } from '@/App/middleware/errorMiddleware';
import { presentationModeStorageMiddleware } from '@/App/middleware/presentationModeStorageMiddleware';
import config from '@/features/config/configSlice';
import displayRules from '@/features/displayRules/displayRulesSlice';
import horoscope from '@/features/horoscope/horoscopeSlice';
import i18n from '@/features/i18n/i18nSlice';
import info from '@/features/info/infoSlice';
import label from '@/features/label/labelSlice';
import people from '@/features/people/peopleSlice';
import person from '@/features/person/personSlice';
import refdata from '@/features/refdata/refdataSlice';
import { initialState as uiInitialState } from '@/features/ui';
import ui from '@/features/ui/uiSlice';
import changePassword from '@/features/user/changePassword/changePasswordSlice';
import deleteProfile from '@/features/user/deleteProfile/deleteProfileSlice';
import forgotPassword from '@/features/user/forgotPassword/forgotPasswordSlice';
import login from '@/features/user/login/loginSlice';
import register from '@/features/user/register/registerSlice';
import resetPassword from '@/features/user/resetPassword/resetPasswordSlice';
import user from '@/features/user/userSlice';
import violations from '@/features/violations/violationsSlice';
import StorageService from '@/services/StorageService';

const rootReducer = combineReducers({
  horoscope,
  i18n,
  user,
  refdata,
  displayRules,
  violations,
  ui,
  login,
  register,
  forgotPassword,
  resetPassword,
  config,
  info,
  changePassword,
  deleteProfile,
  person,
  people,
  label,
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
  const storedPresentationMode = StorageService.getValue(PRESENTATION_MODE_KEY, { enabled: false });
  const presentationModePreloadedState = {
    presentationMode: storedPresentationMode,
  };

  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(appSpinnerMiddleware, errorMiddleware, presentationModeStorageMiddleware),
    preloadedState: {
      ...preloadedState,
      ui: {
        ...uiInitialState,
        ...(preloadedState?.ui || {}),
        ...presentationModePreloadedState,
      },
    },
  });
};

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;

export type AppStore = ReturnType<typeof setupStore>;

export type AppDispatch = AppStore['dispatch'];
